import { SendEmailMutation } from "#src/api/Email.ts";
import {
  DeleteStripeSourceMutation,
  PaymentIntentMutation,
  StripeCustomerQuery,
  UpsertStripeCustomerMutation,
} from "#src/api/Stripe.ts";
import { UpdateUserMutation, UserBillingSummaryQuery } from "#src/api/User.ts";
import { BorderButton } from "#src/components/buttons/BorderButton.tsx";
import { Alert } from "#src/components/forms/Alert.tsx";
import UserModel from "#src/models/UserModel.ts";
import { apiClient, useApi } from "#src/stores/Api.tsx";
import { useSession } from "#src/stores/Session.tsx";
import { useStore } from "#src/stores/Store.tsx";
import { dateFormatLong } from "#src/utils/constants.ts";
import { CreditCardType } from "#src/utils/creditCardType.ts";
import { moment } from "#src/utils/dateTime.ts";
import formatMoney from "#src/utils/formatMoney.ts";
import lazyImport from "#src/utils/lazyImport.ts";
import message from "#src/utils/message.tsx";
import renderEmail from "#src/utils/renderEmail.ts";
import {
  AdyenToken,
  EmailType,
  Level,
  PaymentType,
  StripeType,
  deleteStripeSourceVariables,
  sendEmailVariables,
  updateUserVariables,
  upsertStripeCustomer,
  upsertStripeCustomerVariables,
} from "#types/code-generator.ts";
import type { StripeToken } from "#types/stripeToken.js";
import { Ach, Card } from "@adyen/adyen-web";
import { useStripe } from "@stripe/react-stripe-js";
import { Suspense, useRef, useState } from "react";
import RetryPaymentButton from "../buttons/RetryPaymentButton";
import CardElementContainer from "../checkout/CardElementContainer";
import { InputItem } from "../inputs/InputItem";
import { InputText } from "../inputs/InputText";
import CreditCardIcon from "./CreditCardIcon";
import { Form, useForm } from "./Form";

const AdyenPaymentForm = lazyImport(
  () => import("#src/components/checkout/AdyenPaymentForm.tsx")
);
const StripePaymentForm = lazyImport(
  () => import("#src/components/checkout/StripePaymentForm.tsx")
);

interface Props {
  user: {
    id: string;
    firstName: string;
    stripeToken?: StripeToken;
    adyenToken?: StripeToken;
    stripeCustomerId?: string;
    primaryAccount?: {
      id: string;
      firstName: string;
      email: string;
      lastName: string;
      stripeToken?: StripeToken;
      adyenToken?: StripeToken;
      stripeCustomerId?: string;
    };
    email: string;
    lastName: string;
  };
  onNetworkAction?: () => void;
  showReviewButton?: boolean;
  organization?: {
    id: string;
    title: string;
    subdomain: string;
    stripe: StripeType;
    paymentProcessing?: {
      enableAch: boolean;
    };
    departments: {
      id: string;
    }[];
    stripeAccountId?: string;
    stripeExpressAccountId?: string;
    email: string;
  };
  adding?: boolean;
}

const PaymentDetailsUpdated = (vars) => ({
  subject: `Payment Details Updated - ${vars.organizationName}`,
  html: `<p>Dear ${vars.firstName},</p>

<p>Your payment details have been updated. Your new payment method on file (${vars.brand} ending in ${vars.last4}) is now your default payment method, effective immediately.</p>

<p>Sincerely,<br />
${vars.organizationName}</p>`,
  type: EmailType.PaymentDetailsUpdated,
});

export default function UpdatePaymentForm(props: Props) {
  const store = useStore();
  const form = useForm();
  const session = useSession();
  const stripe = useStripe();
  const cardElement = useRef(null);
  const adyenContainer = useRef<Ach | Card>(null);
  const processingMessage = useRef(null);

  const [changing, setChanging] = useState(false);
  const [changingCard, setChangingCard] = useState(Boolean(props.adding));
  const [changingBank, setChangingBank] = useState(false);
  const [paymentError, setPaymentError] = useState<string>(null);

  const organization = props.organization || store.organization;

  const queryUserBillingSummary = useApi(UserBillingSummaryQuery, {
    skip: !props.user?.id,
    variables: {
      id: props.user?.id,
    },
    refetchOnWindowFocus: true,
  });

  const queryStripeCustomer = useApi(StripeCustomerQuery, {
    skip:
      (organization.stripe !== StripeType.Adyen &&
        !props.user?.stripeCustomerId) ||
      organization.stripe === StripeType.Adyen,
    variables: {
      stripeCustomerId: props.user?.stripeCustomerId,
    },
    refetchOnWindowFocus: true,
  });

  // const stripeCustomer: Stripe.Customer & {
  //   default_source: Stripe.CustomerSource;
  // } = queryStripeCustomer?.stripeCustomer;

  // console.log("stripeCustomer", stripeCustomer);

  function changeCard() {
    setChanging(false);
    setChangingCard(true);
    form.setFieldsValue({ paymentType: PaymentType.Card });
  }

  function onNetworkAction() {
    void queryUserBillingSummary.refetch();
    void queryStripeCustomer.refetch();

    if (props.onNetworkAction) {
      props.onNetworkAction();
    }
  }

  async function changeBank() {
    setChanging(false);
    setChangingBank(true);

    if (organization.stripe !== StripeType.Adyen) {
      const clientSecret = await apiClient(PaymentIntentMutation, {
        variables: {
          userId: props.user.id,
        },
      });

      console.log("clientSecret", clientSecret);

      const result = await stripe.collectBankAccountForSetup({
        clientSecret: clientSecret.paymentIntent,
        params: {
          payment_method_type: "us_bank_account",
          payment_method_data: {
            billing_details: {
              name: `${props.user.firstName} ${props.user.lastName}`,
              email: props.user.email,
            },
          },
        },
      });

      console.log("result", result);
      let processingMessage = null;

      if (result.setupIntent.status === "requires_confirmation") {
        processingMessage = message.loading("Confirming…");
        const confirmed = await apiClient(PaymentIntentMutation, {
          variables: {
            userId: props.user.id,
            setupIntentId: result.setupIntent.id,
          },
        });

        console.log("confirmed", confirmed);
      }

      setChangingCard(false);
      form.setFieldsValue({ paymentType: PaymentType.Card });
      onNetworkAction();

      if (processingMessage) {
        processingMessage();
      }
    }
  }

  function cancelChangeCard() {
    setChangingCard(false);
    setChangingBank(false);
    setChanging(false);
  }

  async function sendEmail(token: { brand: string; last4: string }) {
    const variables = {
      organizationName: organization.title,
      firstName: props.organization
        ? props.organization.title
        : props.user.firstName,
      brand: token.brand,
      last4: token.last4,
      organizationUrl: `https://${organization.subdomain}.captyn.com`,
    };

    const emailData = renderEmail({
      template: PaymentDetailsUpdated,
      variables,
    });

    const emailTo = [];

    if (props.user.email) {
      emailTo.push(props.user.id);
    }

    if (props.organization?.email) {
      emailTo.push(props.organization.email);
    }

    const emailVariables: sendEmailVariables = {
      data: {
        users: emailTo,
        subject: emailData.subject,
        organizationId: organization.id,
        html: emailData.htmlText,
        type: emailData.type,
        smsText: emailData.smsText,
      },
    };

    await apiClient(SendEmailMutation, {
      variables: emailVariables,
    });
  }

  function onSubmit(_adyenToken: AdyenToken = null) {
    if (organization.stripe === StripeType.Adyen) {
      try {
        if (adyenContainer.current) {
          if (adyenContainer.current.isValid) {
            form.setIsSubmitting(true);
            processingMessage.current = message.loading("Updating…");

            adyenContainer.current.submit();
          } else {
            adyenContainer.current.showValidation();
          }
        }
      } catch (e) {
        console.log("e", e);
        message.error(e);
      }

      return;
    }

    void form.validateFields(async (err, values) => {
      if (err) {
        return;
      }

      console.log("values", values);

      if (
        !organization.stripeAccountId &&
        !organization.stripeExpressAccountId &&
        !props.organization
      ) {
        setPaymentError("Unable to add a card to this organization.");
        return;
      }

      const processingMessage = message.loading("Updating…");
      form.setIsSubmitting(true);

      stripe
        .createToken(cardElement.current, {
          name: values.nameOnCard,
        })
        .then((stripeToken) => {
          if (stripeToken.error) {
            throw new Error(stripeToken.error.message);
          }
          return updateUser(stripeToken.token);
        })
        .then((token) => {
          setTimeout(() => {
            void sendEmail(token);
            setChangingCard(false);
          }, 16);
        })
        .catch((error) => {
          setPaymentError(error.message);
        })
        .finally(() => {
          processingMessage();
          void store.updateUser();
          form.setIsSubmitting(false);

          if (props.onNetworkAction) {
            props.onNetworkAction();
          }
        });
    });
  }

  async function updateUser(stripeToken) {
    // console.log("stripeToken", stripeToken);

    let token = {
      id: stripeToken.card?.id,
      brand: stripeToken.card?.brand,
      expMonth: stripeToken.card?.exp_month,
      expYear: stripeToken.card?.exp_year,
      name: stripeToken.card?.name,
      last4: stripeToken.card?.last4,
      tokenId: stripeToken.id,
    };

    if (stripeToken.bank_account) {
      token = {
        id: stripeToken.bank_account.id,
        brand: "Bank Account",
        expMonth: null,
        expYear: null,
        name: stripeToken.bank_account.account_holder_name,
        last4: stripeToken.bank_account.last4,
        tokenId: stripeToken.id,
      };
    }

    const stripeVariables: upsertStripeCustomerVariables = {
      data: {
        userId: props.user.id,
        stripeToken: token,
        organizationId: organization.id,
      },
    };

    const upsert = await apiClient(UpsertStripeCustomerMutation, {
      variables: stripeVariables,
    });

    void queryUserBillingSummary.refetch();

    onNetworkAction();

    if (store.user.level === Level.AccountOwner) {
      const newUser = new UserModel({
        ...store.user,
        stripeToken: token,
        stripeCustomerId: upsert.upsertStripeCustomer.stripeCustomerId,
      });

      store.setCurrentUser({ user: newUser });

      if (session.accountOwner) {
        session.updateAccountOwner(newUser);
      }
    }

    return token;
  }

  async function removeCard() {
    const processingMessage = message.loading("Updating…");
    const today = moment();
    let note = `Card removed on ${today.format(dateFormatLong)}`;

    if (store.user) {
      note += ` by ${store.user.firstName} ${store.user.lastName}`;
    }

    const variables: updateUserVariables = {
      data: {},
      where: {
        id: props.user.id,
      },
    };

    const deleteVariables: deleteStripeSourceVariables = {
      data: {
        userId: props.user.id,
        stripeTokenId: props.user.stripeToken?.id,
      },
    };

    if (organization.stripe === StripeType.Adyen) {
      variables.data.adyenToken = {
        ...props.user.adyenToken,
        id: null,
        note,
        error: null,
      };

      deleteVariables.data.stripeTokenId = props.user.adyenToken.id;
    } else {
      variables.data.stripeToken = {
        ...props.user.stripeToken,
        id: null,
        note,
        error: null,
      };
    }

    await apiClient(UpdateUserMutation, { variables });

    try {
      await apiClient(DeleteStripeSourceMutation, {
        variables: deleteVariables,
      });
    } catch (e) {
      console.log(e);
    }

    processingMessage();
    onNetworkAction();
  }

  function setCardElement(ref) {
    cardElement.current = ref;
  }

  function onReceiveToken(args: {
    adyen: {
      token: upsertStripeCustomer["upsertStripeCustomer"];
      isAppleOrGooglePay: boolean;
    };
  }) {
    console.log("args", args);

    if (!args.adyen.token) {
      setPaymentError("Unable to process payment.");
      form.setIsSubmitting(false);

      if (processingMessage.current) {
        processingMessage.current();
      }

      return;
    }

    onNetworkAction();

    const token: StripeToken = args.adyen.token.adyenToken;

    void sendEmail({
      brand: token.brand,
      last4: token.last4,
    });

    form.setIsSubmitting(false);

    if (processingMessage.current) {
      processingMessage.current();
    }

    setChangingCard(false);
    setChangingBank(false);
    setChanging(false);
    setPaymentError(null);

    if (store.user.level === Level.AccountOwner) {
      const newUser = new UserModel({
        ...store.user,
        adyenToken: token,
      });

      store.setCurrentUser({ user: newUser });

      if (session.accountOwner) {
        session.updateAccountOwner(newUser);
      }
    }
  }

  function onError(e: any) {
    console.error("AdyenPaymentForm e", e);
    form.setIsSubmitting(false);

    if (processingMessage.current) {
      processingMessage.current();
    }

    message.error(
      e.message || "An error occurred while processing your payment."
    );

    setPaymentError(
      e?.message || "An error occurred while processing your payment."
    );
  }

  function setAdyenContainer(ref: Ach | Card) {
    adyenContainer.current = ref;
  }

  let content;
  let currentCardContent;
  let paymentErrorMessage;
  let cardNote;
  let balanceNote;
  let balancePaymentNote;
  const accountBalance = queryUserBillingSummary?.userBillingSummary?.balance;
  let user = props.user;

  if (props.user?.primaryAccount) {
    user = props.user.primaryAccount;
  }

  let token = user?.stripeToken;
  const enableAch =
    organization.paymentProcessing?.enableAch || props.organization;

  if (organization.stripe === StripeType.Adyen) {
    token = user?.adyenToken;
  }

  // console.log("invoices", invoices);
  // console.log("cardError", cardError);

  if (accountBalance > 0) {
    balanceNote = (
      <div className="text-error flex text-sm font-bold">
        <div className="mr-2">
          <i aria-hidden className="far fa-circle-info" />
        </div>
        <div className="flex-1">
          Balance Due • ${formatMoney(accountBalance)}
          {token?.error && (
            <>
              <br />
              {token.error}
            </>
          )}
        </div>
      </div>
    );
    balancePaymentNote = (
      <div className="text-error -mt-2 mb-6 flex text-sm font-bold">
        <div className="mr-2">
          <i aria-hidden className="far fa-circle-info" />
        </div>
        <div className="flex-1">
          Upon saving, the account will immediately be charged the outstanding
          balance of ${formatMoney(accountBalance)}.
        </div>
      </div>
    );
  }

  if (paymentError) {
    paymentErrorMessage = (
      <Alert className="mb-2" type="error" message={paymentError} />
    );
  }

  if (token) {
    cardNote = (
      <>
        {token.note && <p className="text-empty mb-0">{token.note}</p>}

        {organization.stripe !== StripeType.Standard && (
          <div className="text-empty">
            The description on your statement will say “Captyn”.
          </div>
        )}
      </>
    );
  }

  if (changingCard) {
    let paymentElement = <></>;

    if (organization.stripe === StripeType.Adyen) {
      paymentElement = (
        <div data-private data-logrocket className="mb-4">
          <AdyenPaymentForm
            user={props.user}
            onReceiveToken={onReceiveToken}
            setAdyenContainer={setAdyenContainer}
            onError={onError}
            omitGooglePay
            omitApplePay
          />
        </div>
      );
    } else {
      paymentElement = (
        <div data-private data-logrocket>
          <CardElementContainer>
            <StripePaymentForm onReady={setCardElement} />
          </CardElementContainer>
        </div>
      );
    }

    if (form.getFieldValue("paymentType") === PaymentType.Bank) {
      paymentElement = <div></div>;
    }

    content = (
      <Form onSubmit={onSubmit}>
        {paymentErrorMessage}

        {organization.stripe !== StripeType.Adyen &&
          form.getFieldValue("paymentType") !== PaymentType.Bank && (
            <InputItem label="Change Payment Method">
              {form.getFieldDecorator("nameOnCard")(
                <InputText
                  placeholder="Name on Card"
                  disabled={form.isSubmitting}
                />
              )}
            </InputItem>
          )}

        <div data-private data-logrocket>
          {paymentElement}
        </div>
        {balancePaymentNote}
        <div className="mr-4 inline-block">
          <BorderButton type="submit" disabled={form.isSubmitting}>
            Save
          </BorderButton>
        </div>
        <BorderButton onClick={cancelChangeCard} disabled={form.isSubmitting}>
          Cancel
        </BorderButton>
      </Form>
    );
  }

  if (changingBank) {
    if (organization.stripe === StripeType.Adyen) {
      content = (
        <Form onSubmit={onSubmit}>
          {paymentErrorMessage}

          <div data-private data-logrocket className="mb-4">
            <AdyenPaymentForm
              user={props.user}
              onReceiveToken={onReceiveToken}
              setAdyenContainer={setAdyenContainer}
              ach
              onError={onError}
            />
          </div>

          {balancePaymentNote}
          <div className="mr-4 inline-block">
            <BorderButton type="submit" disabled={form.isSubmitting}>
              Save
            </BorderButton>
          </div>
          <BorderButton onClick={cancelChangeCard} disabled={form.isSubmitting}>
            Cancel
          </BorderButton>
        </Form>
      );
    }
  }

  if (changing) {
    content = (
      <div className="flex gap-4">
        <BorderButton onClick={changeCard} disabled={form.isSubmitting}>
          <i className="far fa-credit-card text-organization mr-2" /> Add
          credit/debit
        </BorderButton>

        {enableAch && (
          <BorderButton onClick={changeBank} disabled={form.isSubmitting}>
            <i className="far fa-building-columns text-organization mr-2" /> Add
            bank (ACH)
          </BorderButton>
        )}
      </div>
    );
  }

  let stripeUser = props.user;

  if (props.user?.primaryAccount) {
    stripeUser = props.user.primaryAccount;
  }

  if (organization.stripe === StripeType.Adyen) {
    if (stripeUser?.adyenToken && stripeUser?.adyenToken.id) {
      const card = stripeUser?.adyenToken;
      let allowRemove = !props.showReviewButton && store.user.isAdminOrAbove;

      if (props.organization && store.isAdminArea) {
        allowRemove = false;
      }

      currentCardContent = (
        <>
          <div className="mb-4">
            {store.user.id !== props.user.id &&
              card.brand !== CreditCardType["Bank Account"] && (
                <p className="mb-2">{card.name}</p>
              )}

            {card.brand === CreditCardType["Bank Account"] && (
              <p className="mb-2">{card.bankName}</p>
            )}

            <div className="mb-4 flex">
              <div className="mr-2">
                <CreditCardIcon
                  type={card.brand as CreditCardType}
                  error={accountBalance > 0}
                />
              </div>
              <div className="flex-1">
                Ending in {card.last4}
                <>
                  {card.expMonth && (
                    <>
                      {" "}
                      • Expires {card.expMonth}/{card.expYear}
                    </>
                  )}
                </>
                {cardNote}
                {balanceNote}
              </div>
            </div>
          </div>

          {!changingCard && !changing && !changingBank && (
            <>
              {accountBalance > 0 && (
                <div className="mb-2">
                  <RetryPaymentButton
                    balance={accountBalance}
                    user={props.user}
                    onNetworkAction={onNetworkAction}
                    organization={props.organization}
                  />
                </div>
              )}

              <BorderButton
                onClick={() => {
                  if (!store.organization.paymentProcessing.enableAch) {
                    setChangingCard(true);
                  } else {
                    setChanging(true);
                  }
                }}
                className="mr-4"
                disabled={form.isSubmitting}
              >
                <i className="far fa-arrow-right-arrow-left text-organization mr-2" />{" "}
                <span className="text-dark">Change</span>
              </BorderButton>

              {allowRemove && (
                <BorderButton onClick={removeCard} disabled={form.isSubmitting}>
                  <i className="far fa-trash-can text-organization mr-2" />{" "}
                  <span className="text-dark">Remove</span>
                </BorderButton>
              )}

              {props.showReviewButton && (
                <BorderButton to={`/account/settings#payment`}>
                  <span className="text-dark">Review</span>
                </BorderButton>
              )}
            </>
          )}
        </>
      );
    } else {
      currentCardContent = (
        <>
          <div className="mb-4">
            <p className="mb-0">No payment method on file.</p>
            {cardNote}
            {balanceNote}
          </div>
          {!changingCard && !changingBank && !changing && (
            <div className="flex gap-4">
              <BorderButton onClick={changeCard} disabled={form.isSubmitting}>
                <i className="far fa-credit-card text-organization mr-2" /> Add
                credit/debit
              </BorderButton>

              {enableAch && (
                <BorderButton onClick={changeBank} disabled={form.isSubmitting}>
                  <i className="far fa-building-columns text-organization mr-2" />{" "}
                  Add bank (ACH)
                </BorderButton>
              )}
            </div>
          )}
        </>
      );
    }
  } else {
    if (stripeUser?.stripeToken && stripeUser?.stripeToken.id) {
      const card = stripeUser?.stripeToken;
      const hasNextAction = stripeUser?.stripeToken.nextAction;
      let nextActionUrl;
      let allowRemove = !props.showReviewButton && store.user.isAdminOrAbove;

      if (props.organization && store.isAdminArea) {
        allowRemove = false;
      }

      if (hasNextAction) {
        if (hasNextAction.verify_with_microdeposits) {
          nextActionUrl =
            hasNextAction.verify_with_microdeposits.hosted_verification_url;
        }
      }

      currentCardContent = (
        <>
          <div className="mb-4">
            {store.user.id !== props.user.id &&
              card.brand !== CreditCardType["Bank Account"] && (
                <p className="mb-2">{card.name}</p>
              )}

            {card.brand === CreditCardType["Bank Account"] && (
              <p className="mb-2">Bank Account</p>
            )}

            <div className="mb-4 flex">
              <div className="mr-2">
                <CreditCardIcon
                  type={card.brand as CreditCardType}
                  error={accountBalance > 0}
                />
              </div>
              <div className="flex-1">
                Ending in {card.last4}
                <>
                  {card.expMonth && (
                    <>
                      {" "}
                      • Expires {card.expMonth}/{card.expYear}
                    </>
                  )}
                </>
                {cardNote}
                {balanceNote}
              </div>
            </div>
          </div>

          {hasNextAction && (
            <>
              <div>
                <p className="">
                  <i className="far fa-circle-info text-error mr-2" />{" "}
                  <strong>
                    This payment method requires additional verification.
                  </strong>
                </p>
                <div>
                  <BorderButton
                    type="submit"
                    href={nextActionUrl}
                    target="_blank"
                  >
                    Verify Payment Method
                  </BorderButton>
                </div>
              </div>
            </>
          )}

          {!changingCard && !hasNextAction && (
            <>
              {accountBalance > 0 && (
                <div className="mb-2">
                  <RetryPaymentButton
                    balance={accountBalance}
                    user={props.user}
                    onNetworkAction={onNetworkAction}
                    organization={props.organization}
                  />
                </div>
              )}

              <BorderButton
                onClick={changeCard}
                className="mr-4"
                disabled={form.isSubmitting}
              >
                <i className="far fa-arrow-right-arrow-left text-organization mr-2" />{" "}
                <span className="text-dark">
                  {props.organization && <>Change Payment Method</>}
                  {!props.organization && (
                    <>
                      Change<span className="sm:hidden"> Payment Method</span>
                    </>
                  )}
                </span>
              </BorderButton>

              {allowRemove && (
                <BorderButton onClick={removeCard} disabled={form.isSubmitting}>
                  <i className="far fa-trash-can text-organization mr-2" />{" "}
                  <span className="text-dark">Remove</span>
                </BorderButton>
              )}

              {props.showReviewButton && (
                <BorderButton to={`/account/settings#payment`}>
                  <span className="text-dark">Review</span>
                </BorderButton>
              )}
            </>
          )}
        </>
      );
    } else {
      currentCardContent = (
        <>
          <div className="mb-4">
            <p className="mb-0">No card on file.</p>
            {cardNote}
            {balanceNote}
          </div>
          {!changingCard && (
            <div className="flex gap-4">
              <BorderButton onClick={changeCard} disabled={form.isSubmitting}>
                <i className="far fa-credit-card text-organization mr-2" /> Add
                credit/debit
              </BorderButton>

              {enableAch && (
                <BorderButton onClick={changeBank} disabled={form.isSubmitting}>
                  <i className="far fa-building-columns text-organization mr-2" />{" "}
                  Add bank (ACH)
                </BorderButton>
              )}
            </div>
          )}
        </>
      );
    }
  }

  return (
    <Suspense>
      <div className="max-w-[770px]">
        {currentCardContent}
        {content}
      </div>
    </Suspense>
  );
}
